/**
 * Wallet Modal
 *
 * Custom wallet selection modal using Tailwind CSS.
 * Uses ConnectorKit's useConnector hook.
 */

import { useConnector } from "@solana/connector/react";
import type { WalletConnectorMetadata } from "@solana/connector/react";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

// Wallet icons
import { RightShortIcon } from "../assets/icons/arrows/RightShortIcon";
import { UpRightShortIcon } from "../assets/icons/arrows/UpRightShortIcon";
import { CloseIcon } from "../assets/icons/logistic/CloseIcon";
import phantomIcon from "../assets/wallet-icons/phantom.svg";
import solflareIcon from "../assets/wallet-icons/solflare.svg";
import { AnimatedLogo } from "../components/AnimatedLogo/AnimatedLogo";
import { useGeoRestriction } from "../hooks/geo/useGeoRestriction";

import { useWallet } from "./hooks/useWallet";
import { WalletConnectingModal } from "./WalletConnectingModal";
import type { ConnectingStep } from "./WalletConnectingModal";
import { WalletGeoRestrictionModal } from "./WalletGeoRestrictionModal";

// ============================================================================
// Popular Wallets Configuration
// ============================================================================

interface PopularWallet {
    id: string;
    name: string;
    icon: string;
    installUrl: string;
    /** Optional mobile deep-link builder to open app browser with current dapp URL */
    mobileBrowseDeepLink?: (dappUrl: string, referrerUrl: string) => string;
    /** Matching patterns for connector IDs/names (case-insensitive) */
    matchPatterns: string[];
}

interface WalletItem {
    id: string;
    name: string;
    icon: string;
    installed: boolean;
    connector?: WalletConnectorMetadata;
    installUrl?: string;
}

interface ConnectingWalletInfo {
    id: string;
    name: string;
    icon: string;
}

/**
 * List of popular Solana wallets to display even if not installed.
 * Users can click to install if the wallet isn't detected.
 */
const FEATURED_WALLETS: PopularWallet[] = [
    {
        id: "phantom",
        name: "Phantom",
        icon: phantomIcon,
        installUrl: "https://phantom.app/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://phantom.app/ul/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["phantom"]
    },
    {
        id: "solflare",
        name: "Solflare",
        icon: solflareIcon,
        installUrl: "https://solflare.com/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://solflare.com/ul/v1/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["solflare"]
    },
    {
        id: "backpack",
        name: "Backpack",
        icon: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiByeD0iMjYiIGZpbGw9IiNFMzM0MzAiLz4KPHBhdGggZD0iTTY0IDI4QzQ4LjUzNiAyOCAzNiA0MC41MzYgMzYgNTZWNzJDMzYgODcuNDY0IDQ4LjUzNiAxMDAgNjQgMTAwQzc5LjQ2NCAxMDAgOTIgODcuNDY0IDkyIDcyVjU2QzkyIDQwLjUzNiA3OS40NjQgMjggNjQgMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNNTIgNjBDNTIgNTcuNzkwOSA1My43OTA5IDU2IDU2IDU2SDcyQzc0LjIwOTEgNTYgNzYgNTcuNzkwOSA3NiA2MFY3NkM3NiA3OC4yMDkxIDc0LjIwOTEgODAgNzIgODBINTZDNTMuNzkwOSA4MCA1MiA3OC4yMDkxIDUyIDc2VjYwWiIgZmlsbD0iI0UzMzQzMCIvPgo8L3N2Zz4=",
        installUrl: "https://backpack.app/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://backpack.app/ul/v1/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["backpack"]
    }
];

const AUTO_RECONNECT_TIMEOUT_MS = 5_000;
const MOBILE_USER_AGENT_PATTERN =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

function isMobileBrowser(): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }
    return MOBILE_USER_AGENT_PATTERN.test(navigator.userAgent);
}

function resolveWalletExternalUrl(
    wallet: WalletItem,
    isMobile: boolean
): { url: string; isMobileBrowseLink: boolean } {
    const fallbackUrl = wallet.installUrl ?? "#";
    if (!isMobile || typeof window === "undefined") {
        return { url: fallbackUrl, isMobileBrowseLink: false };
    }

    const popularWallet = FEATURED_WALLETS.find((pw) => pw.id === wallet.id);
    if (!popularWallet?.mobileBrowseDeepLink) {
        return { url: fallbackUrl, isMobileBrowseLink: false };
    }

    return {
        url: popularWallet.mobileBrowseDeepLink(
            window.location.href,
            window.location.origin
        ),
        isMobileBrowseLink: true
    };
}

// ============================================================================
// Context
// ============================================================================

interface WalletModalContextValue {
    /** Whether the wallet modal is currently open */
    showModal: boolean;
    /** Open or close the wallet modal */
    setShowModal: (show: boolean) => void;
    /** Open the wallet modal */
    openModal: () => void;
    /** Close the wallet modal */
    closeModal: () => void;
    /** Whether the connecting flow modal is active (prevents ToS from opening) */
    isConnectingFlow: boolean;
    /** Register an access check (call from apps that have whitelist logic) */
    registerAccessCheck: () => void;
    /** Report the result of the access check */
    reportAccessCheck: (isAllowed: boolean) => void;
}

const WalletModalContext = createContext<WalletModalContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface WalletModalProviderProps {
    children: React.ReactNode;
}

/**
 * Provider for wallet modal state management
 *
 * Renders the wallet selection modal and provides context for programmatic control.
 */
export function WalletModalProvider({
    children
}: WalletModalProviderProps): React.ReactElement {
    const [showModal, setShowModalState] = useState(false);
    const [isMobile] = useState(() => isMobileBrowser());
    const [connectingId, setConnectingId] = useState<string | null>(null);
    const [connectingStep, setConnectingStep] = useState<ConnectingStep | null>(
        null
    );
    const [connectingWallet, setConnectingWallet] =
        useState<ConnectingWalletInfo | null>(null);
    const [showGoToSiteButton, setShowGoToSiteButton] = useState(false);

    // Use the combined useConnector hook
    const {
        connectors,
        connectWallet,
        disconnectWallet,
        isConnected,
        isConnecting
    } = useConnector();

    // Use useWallet for isWalletReady (stabilization delay) and publicKey
    const { isWalletReady, publicKey } = useWallet();

    // Geo restriction check — blocks wallet connection for restricted locations
    const {
        isRestricted: isGeoRestricted,
        ip: geoIp,
        country: geoCountry
    } = useGeoRestriction();

    // Access check injection from main app
    const accessCheckRegisteredRef = useRef(false);
    const [accessCheckResult, setAccessCheckResult] = useState<boolean | null>(
        null
    );

    // Create a unified wallet list: installed wallets + uninstalled popular wallets
    const walletList = useMemo(() => {
        const items: WalletItem[] = [];
        const matchedPopularIds = new Set<string>();

        // First, add all detected/installed connectors
        for (const connector of connectors) {
            const connectorIdLower = connector.id.toLowerCase();
            const connectorNameLower = connector.name.toLowerCase();

            // Check if this connector matches any popular wallet
            const matchedPopular = FEATURED_WALLETS.find((pw) =>
                pw.matchPatterns.some(
                    (pattern) =>
                        connectorIdLower.includes(pattern.toLowerCase()) ||
                        connectorNameLower.includes(pattern.toLowerCase())
                )
            );

            if (matchedPopular) {
                matchedPopularIds.add(matchedPopular.id);
            }

            items.push({
                id: connector.id,
                name: connector.name,
                icon: connector.icon || "",
                installed: true,
                connector
            });
        }

        // Then, add popular wallets that weren't detected (uninstalled)
        for (const popularWallet of FEATURED_WALLETS) {
            if (!matchedPopularIds.has(popularWallet.id)) {
                items.push({
                    id: popularWallet.id,
                    name: popularWallet.name,
                    icon: popularWallet.icon,
                    installed: false,
                    installUrl: popularWallet.installUrl
                });
            }
        }

        return items;
    }, [connectors]);

    // Minimum display time refs for "connecting" and "checking" steps
    const connectingMinElapsedRef = useRef(false);
    const connectingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const checkingMinElapsedRef = useRef(false);
    const checkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeModal = useCallback((): void => {
        setShowModalState(false);
        setConnectingId(null);
        setConnectingStep(null);
        setConnectingWallet(null);
        setShowGoToSiteButton(false);
        setAccessCheckResult(null);
        connectingMinElapsedRef.current = false;
        checkingMinElapsedRef.current = false;
    }, []);

    const openModal = useCallback((): void => {
        setShowModalState(true);
    }, []);

    const setShowModal = useCallback(
        (show: boolean): void => {
            if (show) {
                openModal();
                return;
            }
            closeModal();
        },
        [openModal, closeModal]
    );

    const registerAccessCheck = useCallback((): void => {
        accessCheckRegisteredRef.current = true;
    }, []);

    const reportAccessCheck = useCallback((isAllowed: boolean): void => {
        setAccessCheckResult(isAllowed);
    }, []);

    // Minimum display time for "connecting" step
    const [connectingTick, setConnectingTick] = useState(0);

    useEffect(() => {
        if (connectingStep === "connecting") {
            connectingMinElapsedRef.current = false;
            connectingTimerRef.current = setTimeout(() => {
                connectingMinElapsedRef.current = true;
                setConnectingTick((t) => t + 1);
            }, 1500);
        }
        return (): void => {
            if (connectingTimerRef.current) {
                clearTimeout(connectingTimerRef.current);
                connectingTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    // Auto-reconnection timeout: if ConnectorKit is auto-reconnecting on page refresh
    // and the service worker is dead, isConnecting stays true forever.
    // Reset after a short timeout since no user interaction is needed.
    useEffect(() => {
        if (isConnecting && connectingStep === null) {
            const timer = setTimeout(() => {
                void disconnectWallet();
            }, AUTO_RECONNECT_TIMEOUT_MS);
            return (): void => {
                clearTimeout(timer);
            };
        }
    }, [isConnecting, connectingStep, disconnectWallet]);

    // Step transition: connecting -> checking (when wallet extension approves + min time elapsed)
    useEffect(() => {
        if (connectingStep !== "connecting" || !isConnected) return;
        if (!connectingMinElapsedRef.current) return;
        setConnectingStep("checking");
    }, [isConnected, connectingStep, connectingTick]);

    // Step transition: checking -> connected or not-whitelisted
    // Enforce a minimum display time so the "Checking" step is visible
    useEffect(() => {
        if (connectingStep === "checking") {
            checkingMinElapsedRef.current = false;
            checkingTimerRef.current = setTimeout(() => {
                checkingMinElapsedRef.current = true;
                // Re-trigger the transition effect by updating a state tick
                setCheckingTick((t) => t + 1);
            }, 1500);
        }
        return (): void => {
            if (checkingTimerRef.current) {
                clearTimeout(checkingTimerRef.current);
                checkingTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    const [checkingTick, setCheckingTick] = useState(0);

    useEffect(() => {
        if (connectingStep !== "checking" || !isWalletReady) return;
        if (!checkingMinElapsedRef.current) return; // wait for minimum display time

        if (!accessCheckRegisteredRef.current) {
            // No access check registered (e.g. metrics app) — go straight to connected
            setConnectingStep("connected");
            return;
        }

        if (accessCheckResult === null) return; // still waiting for result

        setConnectingStep(
            accessCheckResult ? "connected" : "not-whitelisted-initial"
        );
    }, [isWalletReady, connectingStep, accessCheckResult, checkingTick]);

    // Step transition: not-whitelisted-initial -> not-whitelisted (after 1.5s)
    const notWhitelistedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    useEffect(() => {
        if (connectingStep === "not-whitelisted-initial") {
            notWhitelistedTimerRef.current = setTimeout(() => {
                setConnectingStep("not-whitelisted");
            }, 1500);
        }
        return (): void => {
            if (notWhitelistedTimerRef.current) {
                clearTimeout(notWhitelistedTimerRef.current);
                notWhitelistedTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    // Auto-close after 3s on "connected" step
    const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    useEffect(() => {
        if (connectingStep === "connected") {
            autoCloseTimerRef.current = setTimeout(() => {
                closeModal();
            }, 3000);
        }
        return (): void => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = null;
            }
        };
    }, [connectingStep, closeModal]);

    // Show "Go to Site Manually" after 3s on "connected" step (fallback)
    const goToSiteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (connectingStep === "connected") {
            goToSiteTimerRef.current = setTimeout(() => {
                setShowGoToSiteButton(true);
            }, 3000);
        }
        return (): void => {
            if (goToSiteTimerRef.current) {
                clearTimeout(goToSiteTimerRef.current);
                goToSiteTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    const handleWalletSelect = async (wallet: WalletItem): Promise<void> => {
        if (isConnected) {
            closeModal();
            return;
        }

        if (connectingId || isConnecting || !wallet.connector?.ready) {
            return;
        }

        // Capture wallet info and enter connecting flow
        setConnectingId(wallet.connector.id);
        setConnectingWallet({
            id: wallet.id,
            name: wallet.name,
            icon: wallet.icon
        });
        setConnectingStep("connecting");

        try {
            await connectWallet(wallet.connector.id);
        } catch {
            // Connection rejected — reset to wallet list
            setConnectingStep(null);
            setConnectingWallet(null);
            setConnectingId(null);
        }
    };

    const isConnectingFlow = connectingStep !== null;

    const value: WalletModalContextValue = {
        showModal,
        setShowModal,
        openModal,
        closeModal,
        isConnectingFlow,
        registerAccessCheck,
        reportAccessCheck
    };

    return (
        <WalletModalContext.Provider value={value}>
            {children}

            {/* Geo Restriction Modal */}
            {showModal && isGeoRestricted && (
                <WalletGeoRestrictionModal
                    ip={geoIp}
                    country={geoCountry}
                    onClose={closeModal}
                />
            )}

            {/* Connecting Flow Modal */}
            {showModal &&
                !isGeoRestricted &&
                connectingStep !== null &&
                connectingWallet !== null && (
                    <WalletConnectingModal
                        step={connectingStep}
                        walletName={connectingWallet.name}
                        walletIcon={connectingWallet.icon}
                        walletAddress={publicKey?.toString()}
                        onClose={closeModal}
                        onGoToSite={closeModal}
                        showGoToSite={showGoToSiteButton}
                    />
                )}

            {/* Wallet Selection Modal */}
            {showModal && !isGeoRestricted && connectingStep === null && (
                <>
                    {/* Blur Background */}
                    <div
                        className={`
                            fixed inset-0 bg-[#111114]/50 transition-opacity duration-300 z-40 backdrop-blur
                            ${showModal ? "opacity-100" : "opacity-0 pointer-events-none"}
                        `}
                        onClick={closeModal}
                        aria-hidden="true"
                    />
                    {/* Modal Content */}
                    <div
                        className="panel fixed z-50
                          w-full inset-0 m-auto max-w-screen-lg h-fit"
                        onClick={(e): void => e.stopPropagation()}
                    >
                        <div className="menu rounded-b-md flex flex-col w-full gap-xs overflow-hidden">
                            {/* Header */}
                            <div className="flex w-full items-center justify-between px-md pt-md pb-xs">
                                <h2 className="text-h2 font-bold text-foreground-base">
                                    Connect Wallet
                                </h2>
                                <button
                                    onClick={closeModal}
                                    className="text-foreground-base
                                        hover:text-primary-base
                                        active:text-primary-muted
                                        transition-colors duration-200
                                        cursor-pointer"
                                    aria-label="Close"
                                >
                                    <CloseIcon className="w-auto h-lg" />
                                </button>
                            </div>

                            {/* Divider */}
                            <div className="px-sm">
                                <div className="border-b sm:border-b-2 rounded-full border-foreground-imprint" />
                            </div>

                            {/* Wallet List */}
                            <div className="flex flex-col w-full py-xs max-h-[336px] sm:max-h-[348px] md:max-h-[378px] overflow-y-auto">
                                {walletList.map((wallet) => {
                                    const isThisConnecting =
                                        connectingId === wallet.id;
                                    const isAnyConnecting =
                                        connectingId !== null || isConnecting;

                                    // Installed wallet - can connect
                                    if (wallet.installed && wallet.connector) {
                                        const isConnectorReady =
                                            wallet.connector.ready;
                                        return (
                                            <button
                                                key={wallet.id}
                                                onClick={(): void =>
                                                    void handleWalletSelect(
                                                        wallet
                                                    )
                                                }
                                                aria-selected={isThisConnecting}
                                                disabled={
                                                    !isConnectorReady ||
                                                    (isAnyConnecting &&
                                                        !isThisConnecting)
                                                }
                                                className="menu-item group items-center w-full
                                                    aria-selected:bg-primary-base
                                                    aria-selected:no-underline
                                                    disabled:hover:bg-transparent"
                                            >
                                                <div className="flex flex-row items-center w-full gap-sm">
                                                    {wallet.icon && (
                                                        <img
                                                            src={wallet.icon}
                                                            alt={wallet.name}
                                                            className="w-xl h-xl rounded-xs"
                                                        />
                                                    )}
                                                    <div className="flex-1 text-left">
                                                        <span
                                                            className="font-bold
                                                            group-aria-selected:text-foreground-reverse
                                                            group-aria-selected:no-underline"
                                                        >
                                                            {wallet.name}
                                                        </span>
                                                        <p
                                                            className="text-xs font-medium
                                                            text-foreground-muted
                                                            group-aria-selected:text-foreground-imprint
                                                            group-aria-selected:no-underline
                                                            group-hover:text-foreground-imprint
                                                            group-active:text-foreground-imprint
                                                            group-disabled:text-foreground-imprint
                                                            hidden sm:block"
                                                        >
                                                            {isConnectorReady
                                                                ? "Detected"
                                                                : "Unavailable"}
                                                        </p>
                                                    </div>
                                                </div>
                                                {isThisConnecting ? (
                                                    <AnimatedLogo
                                                        className="w-auto h-md
                                                        group-aria-selected:text-foreground-reverse"
                                                    />
                                                ) : (
                                                    <RightShortIcon
                                                        className="w-auto h-md
                                                        text-foreground-muted
                                                        group-hover:text-foreground-reverse
                                                        group-active:text-foreground-reverse
                                                        group-disabled:text-foreground-reverse"
                                                    />
                                                )}
                                            </button>
                                        );
                                    }

                                    // Uninstalled wallet - show install link
                                    const { url, isMobileBrowseLink } =
                                        resolveWalletExternalUrl(
                                            wallet,
                                            isMobile
                                        );
                                    return (
                                        <a
                                            key={wallet.id}
                                            href={url}
                                            target={
                                                isMobileBrowseLink
                                                    ? "_self"
                                                    : "_blank"
                                            }
                                            rel={
                                                isMobileBrowseLink
                                                    ? undefined
                                                    : "noopener noreferrer"
                                            }
                                            className="menu-item group items-center w-full"
                                        >
                                            {wallet.icon && (
                                                <img
                                                    src={wallet.icon}
                                                    alt={wallet.name}
                                                    className="w-xl h-xl rounded-xs opacity-100 sm:opacity-50 sm:group-hover:opacity-90 transition-opacity duration-300"
                                                />
                                            )}
                                            <div className="flex flex-row items-center w-full gap-sm">
                                                <div className="flex-1 text-left">
                                                    <span className="font-bold">
                                                        {wallet.name}
                                                    </span>
                                                    <p
                                                        className="text-xs font-medium
                                                            text-foreground-muted
                                                            group-aria-selected:text-foreground-imprint
                                                            group-hover:text-foreground-imprint
                                                            group-active:text-foreground-imprint
                                                            group-disabled:text-foreground-imprint
                                                            hidden sm:block"
                                                    >
                                                        {isMobileBrowseLink
                                                            ? "Open in wallet app"
                                                            : "Not installed"}
                                                    </p>
                                                </div>
                                                <span
                                                    className="flex flex-row items-center gap-2xs text-xs font-medium
                                                            text-foreground-muted
                                                            group-aria-selected:text-foreground-reverse
                                                            group-hover:text-foreground-reverse
                                                            group-active:text-foreground-reverse
                                                            group-disabled:text-foreground-reverse"
                                                >
                                                    {isMobileBrowseLink
                                                        ? "Open"
                                                        : "Install"}
                                                    <UpRightShortIcon className="w-auto h-xs" />
                                                </span>
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>

                            {/* Footer */}
                            <div className="px-md pb-md">
                                <p className="text-xs text-foreground-muted/70 text-center">
                                    By connecting, you agree to our{" "}
                                    <a
                                        href="https://solomonlabs.org/terms-of-service"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link"
                                    >
                                        Terms of Service
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </WalletModalContext.Provider>
    );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access wallet modal controls
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, showModal } = useWalletModal();
 *
 * return (
 *   <button onClick={openModal}>Connect Wallet</button>
 * );
 * ```
 */
export function useWalletModal(): WalletModalContextValue {
    const context = useContext(WalletModalContext);
    if (!context) {
        throw new Error(
            "useWalletModal must be used within a WalletModalProvider"
        );
    }
    return context;
}

/**
 * Alias for useWalletModal - provides Jupiter-compatible API
 * @deprecated Use useWalletModal instead
 */
export const useUnifiedWalletContext = useWalletModal;
